// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { observable, action } from 'mobx'
import { IStores } from '.'
import { sendBookingAnalyticEvent } from '../services/analyticsService'
import { getFromApi } from '../services/apiService'

const build_LOCATIONS_URL = () => '/v5/reserve/locations'

export interface ICountry {
  code: string
}

export interface ICity {
  reference: string;
  priority: number;
  imageUrl: string;
  timezone: string;
  name: string;
  available: true;
  message: string;
  country: ICountry;
}

export interface IBusinessTime {
  start: string,
  end: string,
}

export interface ILocationSchedule {
  reference: string | null,
  monday: IBusinessTime[] | null,
  tuesday: IBusinessTime[] | null,
  wednesday: IBusinessTime[] | null,
  thursday: IBusinessTime[] | null,
  friday: IBusinessTime[] | null,
  saturday: IBusinessTime[] | null,
  sunday: IBusinessTime[] | null,
  holidays: string[] | null
}

export interface ILocation {
  reference: string;
  priority: number;
  name: string;
  imageUrl: string;
  iconUrl: string;
  color: string;
  timezone: string;
  available: true;
  message: string;
  latitude: number;
  longitude: number;
  address: string;
  rangePolygone: string | null;
  city: ICity;
  pickupSchedule: ILocationSchedule;
  returnSchedule: ILocationSchedule;
  productReferences: string[];
  descriptionReference?: string | null;
}

class LocationsStore {
  store: IStores

  @observable loading = false

  @observable locations: ILocation[] = []
  @observable selectedCity: ICity | null = null
  @observable selectedLocation: ILocation | null = null

  @observable deviceLatitude: number | null = null
  @observable deviceLongitude: number | null = null

  constructor(store: IStores) {
    this.store = store
  }

  @action initialise = async (): Promise<void> => {
    this.loading = true

    const response = await getFromApi(build_LOCATIONS_URL())

    this.locations = response && response.status === 200 ? response.data?.locations || [] : []

    if (this.locations.length === 1) {
      this.selectLocation(this.locations[0])
    }
    this.loading = false
  }

  @action selectCity = async (
    city: ICity | null,
    inverseSelection = false
  ) => {
    this.loading = true

    if (
      city === null ||
      (inverseSelection &&
        city?.reference === this.selectedCity?.reference)
    ) {
      this.selectedCity = null
    } else {
      this.selectedCity = city
      sendBookingAnalyticEvent('book_step_1_select_city', this.store.productsStore.selectedProduct, city)
      const locations = this.locations.filter(l => (l.city.reference === city.reference))

      if (locations.length === 1) {
        this.selectLocation(locations[0])
      }
    }
    this.loading = false
  }

  @action selectLocation = async (
    location: ILocation | null,
    inverseSelection = false
  ) => {
    this.loading = true

    if (
      location === null ||
      (inverseSelection &&
        location?.reference === this.selectedLocation?.reference)
    ) {
      this.selectedLocation = null
    } else {
      this.selectedLocation = location
      sendBookingAnalyticEvent('book_step_1_select_location', this.store.productsStore.selectedProduct, location?.city, location)
    }
    this.loading = false
  }

  @action selectLocationByReference = async (
    locationReference: string,
    inverseSelection = false
  ) => {
    await this.selectLocation(
      this.store.locationsStore.locations.find(
        location => location.reference === locationReference
      ) || null,
      inverseSelection
    )
  }

  @action selectDeviceGeoLocation = async (geolocation: any) => {
    this.deviceLatitude = geolocation.latitude
    this.deviceLongitude = geolocation.longitude
  }
}

export default LocationsStore
